import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, Users, Flame, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface CountdownTimerProps {
  hours: number;
  onExpire?: () => void;
}

export const CountdownTimer = ({ hours, onExpire }: CountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: hours,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const targetTime = Date.now() + (hours * 60 * 60 * 1000);
    
    const timer = setInterval(() => {
      const now = Date.now();
      const difference = targetTime - now;

      if (difference <= 0) {
        clearInterval(timer);
        onExpire?.();
        return;
      }

      setTimeLeft({
        hours: Math.floor(difference / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [hours, onExpire]);

  return (
    <div className="flex items-center gap-2">
      <Clock className="h-4 w-4 text-orange-600 animate-pulse" />
      <div className="flex gap-1 font-mono font-bold text-lg">
        <div className="bg-foreground text-background px-2 py-1 rounded">
          {String(timeLeft.hours).padStart(2, '0')}
        </div>
        <span>:</span>
        <div className="bg-foreground text-background px-2 py-1 rounded">
          {String(timeLeft.minutes).padStart(2, '0')}
        </div>
        <span>:</span>
        <div className="bg-foreground text-background px-2 py-1 rounded">
          {String(timeLeft.seconds).padStart(2, '0')}
        </div>
      </div>
    </div>
  );
};

export const LimitedSpotsBanner = () => {
  const [spotsLeft, setSpotsLeft] = useState(23);

  useEffect(() => {
    // Simulate spots decreasing
    const interval = setInterval(() => {
      setSpotsLeft(prev => {
        if (prev <= 15) return 15;
        return prev - 1;
      });
    }, 45000); // Every 45 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-3"
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <Badge className="bg-white text-orange-600 hover:bg-white font-bold">
            <Flame className="h-3 w-3 mr-1" />
            Limited Time Offer
          </Badge>
          <p className="font-semibold">
            Only <span className="text-2xl font-bold mx-2">{spotsLeft}</span> spots available for new clients this week
          </p>
          <CountdownTimer hours={48} />
        </div>
      </div>
    </motion.div>
  );
};

interface StockIndicatorProps {
  available: number;
  total: number;
  label?: string;
}

export const StockIndicator = ({ available, total, label = "slots" }: StockIndicatorProps) => {
  const percentage = (available / total) * 100;
  const isLow = percentage < 30;
  const isCritical = percentage < 15;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label} available:</span>
        <span className={`font-bold ${isCritical ? 'text-red-600' : isLow ? 'text-orange-600' : 'text-green-600'}`}>
          {available} / {total}
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={`h-full ${
            isCritical ? 'bg-red-600' : 
            isLow ? 'bg-orange-500' : 
            'bg-green-500'
          }`}
        />
      </div>
      {isLow && (
        <p className="text-xs text-orange-600 font-medium">
          ⚠️ Filling up fast! Book now to secure your spot
        </p>
      )}
    </div>
  );
};

export const LiveActivityCounter = () => {
  const [activeUsers, setActiveUsers] = useState(127);
  const [sessionsToday, setSessionsToday] = useState(48);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveUsers(prev => prev + Math.floor(Math.random() * 3) - 1);
      setSessionsToday(prev => prev + Math.floor(Math.random() * 2));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="border-2 border-primary/20">
      <CardContent className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-full">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Browsing now</div>
              <div className="text-xl font-bold text-green-600">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={activeUsers}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeUsers}
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Sessions today</div>
              <div className="text-xl font-bold text-blue-600">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={sessionsToday}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {sessionsToday}
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const PriceIncreaseWarning = ({ daysLeft = 5 }: { daysLeft?: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-lg p-4"
    >
      <div className="flex items-start gap-3">
        <div className="bg-orange-100 p-2 rounded-full flex-shrink-0">
          <TrendingUp className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-orange-900 mb-1">
            Price Increase in {daysLeft} Days
          </div>
          <p className="text-sm text-orange-700">
            Lock in current rates now. Prices will increase by 20% after this week.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export const ViewersCounter = ({ count = 3 }: { count?: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-3 py-1"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
      </span>
      <span className="text-sm font-medium">
        <span className="font-bold text-primary">{count}</span> people viewing this now
      </span>
    </motion.div>
  );
};
